import { C } from "../lib/colors.js";
import { continueState, resolutionMomentKey } from "../lib/forecast/preprocessContinue.js";
import { editBufferOf } from "../lib/forecast/multiway.js";
import ResolutionMomentPicker from "./ResolutionMomentPicker.jsx";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { Button } from "./ui/Primitives.jsx";
import { IconPencil } from "./ui/Icons.jsx";

/**
 * "Confirm and forecast". It never fires by itself: the panel waits for the user. With a typed
 * resolution date pending it applies that date instead, exactly as the picker's apply would.
 */
function ConfirmButton({ pp, onDismiss, applyPending = null, pendingInvalid = false }) {
  const st = continueState(pp, { applyPending: !!applyPending, pendingInvalid });
  if (!st.visible) return null;
  return (
    <Button
      {...ui("preprocess.continue")}
      // With a typed date pending it applies that date instead; the later spread wins.
      {...(applyPending ? ui("resolution.custom-apply") : {})}
      variant="primary"
      data-popup-primary=""
      {...(st.escapeTarget ? { "data-popup-cancel": "" } : {})}
      disabled={st.disabled}
      title={st.disabled ? "Enter a valid date and time" : undefined}
      onClick={applyPending || onDismiss}
    >
      {applyPending ? "Use this date and forecast" : "Confirm and forecast"}
    </Button>
  );
}

/**
 * @param {object} props
 * @param {object} props.pp  the pending-preprocess state for this submission
 * @param {string} props.theme
 * @param {boolean} props.bugOpen  the bug-report modal is open (it keeps focus)
 * @param {(updater: Function) => void} props.updatePp  setter for `pp`
 * @param {() => void} props.onDismiss  confirm and close
 * @param {() => void} props.onEditResubmit  re-run with the edited question
 * @param {(choice: object) => void} props.onChooseResolution  apply a resolution moment
 */
export default function PreprocessPanel({
  pp,
  bugOpen,
  updatePp,
  onDismiss,
  onEditResubmit,
  onChooseResolution,
}) {
  // A stray click outside never throws away an edit or a pending choice.
  const backdropDismisses = !pp.submitting && !pp.awaitingChoice && !pp.editing && !pp.paused;
  const isTrigger = !!pp.branchTrigger;
  const startEdit = () => updatePp((p) => ({ ...p, editing: true, paused: true }));
  const cancelEdit = () =>
    updatePp((p) => ({
      ...p,
      editing: false,
      editText: editBufferOf(p.question.question_text || p.original, p.routing),
      submitError: null,
    }));
  const criteria = String(pp.question?.resolution_criteria || "").trim();

  const editButton = !pp.editing ? (
    <Button
      {...ui("preprocess.edit-question")}
      data-testid="pp-edit-question"
      variant="ghost"
      icon={<IconPencil />}
      onClick={startEdit}
    >
      Edit
    </Button>
  ) : null;
  const actions = (applyPending, pendingInvalid) =>
    pp.editing ? null : (
      <>
        {editButton}
        <ConfirmButton
          pp={pp}
          onDismiss={onDismiss}
          applyPending={applyPending}
          pendingInvalid={pendingInvalid}
        />
      </>
    );
  const criteriaBlock =
    criteria && !pp.editing ? (
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 12, color: C.gray, marginBottom: 4 }}>How it resolves</div>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: C.grayL }}>{criteria}</div>
      </div>
    ) : null;

  return (
    <div
      {...surface("preprocess", pp?.clientId)}
      {...ui("preprocess.backdrop")}
      {...(backdropDismisses ? ui("preprocess.dismiss") : {})}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 400,
        background: C.modalOverlay,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        animation: "popupOverlayIn 0.22s ease-out",
      }}
      onClick={() => {
        if (backdropDismisses) onDismiss();
      }}
    >
      <div
        {...ui("preprocess.dialog")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pp-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !pp.editing) {
            // Stopped here so App's document-level Enter shortcut never fires as well.
            e.stopPropagation();
            // A focused control inside (Edit, Confirm) keeps its own Enter action.
            if (e.target !== e.currentTarget) return;
            e.preventDefault();
            // The panel itself: press its primary action, which applies a typed date if one is
            // pending. A held panel with no date typed has none, so Enter does nothing.
            e.currentTarget.querySelector("[data-popup-primary]:not(:disabled)")?.click();
          } else if (e.key === "Escape" && pp.editing && !pp.submitting) {
            e.preventDefault();
            cancelEdit();
          }
        }}
        tabIndex={0}
        // Runs on every render (a fresh callback), so it must never pull focus out of a
        // field inside the panel — the resolution date input, the edit textarea.
        ref={(el) => {
          if (el && !pp.editing && !bugOpen && !el.contains(document.activeElement)) {
            el.focus();
          }
        }}
        style={{
          background: C.panel,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          boxShadow: C.shadowBox,
          padding: "28px 32px",
          width: 600,
          maxWidth: "92vw",
          maxHeight: "90vh",
          overflowY: "auto",
          boxSizing: "border-box",
          outline: "none",
          animation: "popupPanelIn 0.32s cubic-bezier(0.16,1,0.3,1) both",
        }}
      >
        <h2
          id="pp-title"
          style={{ margin: 0, fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: C.white }}
        >
          {isTrigger ? "Check your trigger" : "Check your question"}
        </h2>
        <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.5, color: C.gray }}>
          {isTrigger
            ? "We reworded your trigger so it can be resolved objectively. Check that it still means what you intended."
            : "We reworded your question so it can be forecast and resolved objectively. Check that it still means what you intended."}
        </p>

        {!pp.editing ? (
          <div
            {...ui("preprocess.edit-question")}
            onClick={startEdit}
            title="Click to edit"
            style={{
              marginTop: 20,
              padding: "14px 16px",
              background: C.rowInactive,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 17,
              fontWeight: 500,
              color: C.white,
              lineHeight: 1.45,
              cursor: "text",
            }}
          >
            {pp.question.question_text || pp.original}
          </div>
        ) : (
          <div style={{ marginTop: 20 }}>
            <textarea
              {...ui("preprocess.question-input")}
              autoFocus={!bugOpen}
              disabled={pp.submitting}
              value={pp.editText}
              aria-label={isTrigger ? "Trigger" : "Question"}
              onChange={(e) =>
                updatePp((p) => ({
                  ...p,
                  editText: e.target.value,
                  submitError: null,
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onEditResubmit();
                }
              }}
              style={{
                width: "100%",
                background: C.inputBg,
                border: `1px solid ${C.accent}`,
                borderRadius: 8,
                color: C.white,
                font: "inherit",
                fontSize: 17,
                padding: "12px 14px",
                resize: "vertical",
                minHeight: 84,
                outline: "none",
                boxSizing: "border-box",
                lineHeight: 1.45,
                opacity: pp.submitting ? 0.6 : 1,
              }}
            />
            <div
              style={{
                marginTop: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 12, color: C.gray }}>
                Enter to update · Shift+Enter for a new line · Esc to cancel
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {!pp.submitting && (
                  <Button {...ui("preprocess.edit-cancel")} variant="ghost" onClick={cancelEdit}>
                    Cancel
                  </Button>
                )}
                <Button
                  {...ui("preprocess.edit-submit")}
                  variant="primary"
                  data-popup-primary=""
                  disabled={pp.submitting}
                  onClick={onEditResubmit}
                >
                  {pp.submitting ? "Checking…" : isTrigger ? "Update trigger" : "Update question"}
                </Button>
              </div>
            </div>
          </div>
        )}
        {pp.submitError && (
          <div role="alert" style={{ marginTop: 8, fontSize: 13, color: C.danger }}>
            {pp.submitError}
          </div>
        )}

        {isTrigger ? (
          <>
            {criteriaBlock}
            {!pp.editing && !pp.awaitingChoice && (
              <div style={{ marginTop: 28, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {actions(null, false)}
              </div>
            )}
          </>
        ) : (
          <ResolutionMomentPicker
            // Remount when the deadline itself changes (an edit, a re-preprocess), so the
            // custom-date field starts from the moment now shown.
            key={resolutionMomentKey(pp.question)}
            question={pp.question}
            awaiting={!!pp.awaitingChoice}
            // Inert while the question is being edited: a pick would run the saved wording,
            // not the text being composed.
            disabled={!!pp.submitting || !!pp.editing}
            onChoose={onChooseResolution}
            // `editingDate` (the moment the editor opened on) keeps the panel open if the
            // forecast completes while a date is being changed (closesOnCompletion).
            onEditStart={() =>
              updatePp((p) =>
                p ? { ...p, paused: true, editingDate: resolutionMomentKey(p.question) } : p,
              )
            }
            renderAction={actions}
          >
            {criteriaBlock}
          </ResolutionMomentPicker>
        )}
      </div>
    </div>
  );
}
